import { useEffect, useRef } from "react";
import { Button, useToast } from "@gradebook/ui";
import {
  RUNNING_COMMIT,
  VERSION_POLL_MS,
  fetchDeployedVersion,
  isNewVersionAvailable,
} from "@/lib/versionCheck";

/**
 * Offers a reload when the Worker starts serving a build newer than this one.
 *
 * Renders nothing. It exists so that a deploy is something the app mentions
 * while every page still works, rather than something the person discovers by
 * clicking a route whose chunk has been deleted out from under them —
 * `lib/staleDeploy.ts` catches that, but only after the fact, and a reload it
 * triggers costs whatever was unsaved on the page.
 *
 * Mounted once, at the app root inside `ToastProvider`. Deliberately outside
 * `ProtectedRoute`: a deploy invalidates a signed-out tab sitting on the login
 * page exactly as much as a signed-in one.
 */
export function NewVersionNotice(): null {
  const toast = useToast();
  // A ref, not state: the toast is fired at most once per page and nothing
  // renders from this, so flipping it must not schedule a render.
  const announced = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    async function check(): Promise<void> {
      if (announced.current) return;

      const deployed = await fetchDeployedVersion(controller.signal);
      // Re-checked after the await: the tab may have been hidden and shown
      // again while this request was in flight, and two toasts saying the same
      // thing is worse than one.
      if (announced.current) return;
      if (!isNewVersionAvailable(RUNNING_COMMIT, deployed)) return;

      announced.current = true;
      toast.info(
        <span className="flex items-center gap-3">
          A new version is available.
          <Button size="sm" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </span>,
        // Pinned. Unlike a save confirmation this is not describing something
        // that already happened — it is asking for something, and a request
        // that times out while being read is a request nobody acts on.
        { duration: null },
      );
    }

    // Whenever the tab is brought forward, which is when a long-lived tab is
    // most likely to be out of date and about to be used. The interval is the
    // backstop for a tab left open in the foreground.
    function onVisibilityChange(): void {
      if (document.visibilityState === "visible") void check();
    }

    void check();
    const timer = window.setInterval(() => void check(), VERSION_POLL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [toast]);

  return null;
}
